import {
    FormCheckbox,
    FormInput,
    FormInputPassword,
    FormTextArea,
    type GroupedOptions,
    type SelectOption
} from '@auviknetworks/iceberg-ui';
import { FormFieldsGroup } from 'application/Shared/Components/Form/FormFieldsGroup';
import { FormKeyValue } from 'application/Shared/Components/Form/FormKeyValue';
import { FormSelectHandler } from 'application/Shared/Components/Form/FormSelectHandler';
import { TFunction } from 'i18next';

import { type FieldSchemaJson, type StaticOptions } from './Field.type';
import { type Fields } from './types';

function registerProps(schema: FieldSchemaJson, t: TFunction) {
    return schema.optional === false
        ? {
              registerProps: {
                  required: t('Form.field.required', {
                      label: schema.label || schema.name
                  })
              }
          }
        : {};
}
/**
 * A mapping of field types used by the Fields Factory. Naming is based on Workato Schema.
 * https://docs.workato.com/developing-connectors/sdk/sdk-reference/schema.html#control-types
 */
export const fields: Fields = {
    checkbox: {
        Component: FormCheckbox,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked as
                        | boolean
                        | undefined,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t)
                };
            }

            return {
                defaultChecked: false,
                helperText: schema.hint,
                label: t(schema.label || schema.name),
                name: schema.name,
                ...registerProps(schema, t)
            };
        }
    },
    date: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    inputType: 'date',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t)
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'date',
                label: t(schema.label || schema.name),
                name: schema.name,
                ...registerProps(schema, t)
            };
        }
    },
    date_time: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    inputType: 'datetime-local',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t)
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'datetime-local',
                label: t(schema.label || schema.name),
                name: schema.name,
                ...registerProps(schema, t)
            };
        }
    },
    email: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    inputType: 'email',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    pattern: '[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,4}$',
                    placeholder: 'e.g., user@example.com',
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t)
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'email',
                label: t(schema.label || schema.name),
                name: schema.name,
                pattern: '[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,4}$',
                placeholder: 'e.g., user@example.com',
                ...registerProps(schema, t)
            };
        }
    },
    field_group: {
        Component: FormFieldsGroup,
        Transformer: (schema, t) => ({
            children: schema.properties as FieldSchemaJson[],
            disabled: schema.control!.disabled as boolean | undefined,
            helperText: schema.control!.helperText,
            label: t(schema.label || schema.name),
            name: schema.name,
            readOnly: schema.control!.readOnly as boolean | undefined,
            required: schema.optional !== true
        })
    },
    integer: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    inputType: 'number',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    pattern: 'd*',
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t),
                    // This is setting the step to full numbers only
                    step: 1
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'number',
                label: t(schema.label || schema.name),
                name: schema.name,
                pattern: 'd*',
                ...registerProps(schema, t),
                // This is setting the step to full numbers only
                step: 1
            };
        }
    },
    key_value: {
        Component: FormKeyValue,
        Transformer: (schema) => ({
            label: schema.label || schema.name,
            name: schema.name,
            placeholder: 'schema.empty_list_title'
        })
    },
    number: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    inputType: 'number',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t)
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'number',
                label: t(schema.label || schema.name),
                name: schema.name,
                pattern: 'd*',
                ...registerProps(schema, t)
            };
        }
    },
    password: {
        Component: FormInputPassword,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t)
                };
            }

            return {
                helperText: schema.hint,
                label: t(schema.label || schema.name),
                name: schema.name,
                ...registerProps(schema, t)
            };
        }
    },
    phone: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    inputType: 'tel',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    placeholder: 'e.g., +1 (1234) 567 890',
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t)
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'tel',
                label: t(schema.label || schema.name),
                name: schema.name,
                placeholder: 'e.g., +1 (1234) 567 890',
                ...registerProps(schema, t)
            };
        }
    },
    select: {
        Component: FormSelectHandler,
        Transformer: (schema, t) => {
            const baseProps = {
                isClearable: schema.optional === true,
                isMulti: !!schema.control?.allowMultipleSelect as
                    | boolean
                    | undefined,
                label: t(schema.label || schema.name),
                name: schema.name,
                required: schema.optional === false
            };
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                const additionalProps = {
                    ...baseProps,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    readOnly: schema.control!.readOnly as boolean | undefined
                };

                // @ts-expect-error fix type
                if (schema.control!.options.type === 'api') {
                    // @ts-expect-error fix type
                    const { operation } = schema.control!.options;

                    return {
                        ...additionalProps,
                        operation
                    };
                }

                return {
                    ...additionalProps,
                    options: (
                        schema.control!.options! as StaticOptions
                    ).values.map((option: unknown) => option) as (
                        | SelectOption
                        | GroupedOptions
                    )[],
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t)
                };
            }

            return {
                ...baseProps,
                helperText: schema.hint,
                options: schema.pick_list!.map(([value, label]) => ({
                    label,
                    value
                })),
                ...registerProps(schema, t)
            };
        }
    },
    subdomain: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    inputType: 'text',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    pattern: '^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$',
                    placeholder: 'e.g., mysubdomain',
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t),
                    suffix: '.example.com'
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'text',
                label: t(schema.label || schema.name),
                name: schema.name,
                pattern: '^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$',
                placeholder: 'e.g., mysubdomain',
                ...registerProps(schema, t),
                suffix: '.example.com'
            };
        }
    },
    text: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    inputType: 'text',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t)
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'text',
                label: t(schema.label || schema.name),
                name: schema.name,
                ...registerProps(schema, t)
            };
        }
    },
    'text-area': {
        Component: FormTextArea,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t)
                };
            }

            return {
                helperText: schema.hint,
                label: t(schema.label || schema.name),
                name: schema.name,
                ...registerProps(schema, t)
            };
        }
    },
    url: {
        Component: FormInput,
        Transformer: (schema, t) => {
            // We check if the field has our definition or is an original workato field
            if ('control' in schema) {
                return {
                    defaultChecked: schema.control!.defaultChecked,
                    disabled: schema.control!.disabled as boolean | undefined,
                    helperText: schema.control!.helperText,
                    inputType: 'url',
                    label: t(schema.label || schema.name),
                    name: schema.name,
                    pattern: 'https://.*',
                    placeholder: 'e.g., https://example.com',
                    readOnly: schema.control!.readOnly as boolean | undefined,
                    ...registerProps(schema, t),
                    suffix: '.example.com'
                };
            }

            return {
                helperText: schema.hint,
                inputType: 'url',
                label: t(schema.label || schema.name),
                name: schema.name,
                pattern: 'https://.*',
                placeholder: 'e.g., https://example.com',
                ...registerProps(schema, t),
                suffix: '.example.com'
            };
        }
    }
};
